import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonIcon, IonSpinner } from "@ionic/react";
import { useRef, useState } from "react";
import { addOutline, documentOutline, downloadOutline, imageOutline, trashOutline } from "ionicons/icons";
import { ReportWeek, ReportWeekAttachment } from "../../models/reportModels";
import ReportService from "../../data/services/ReportService";
import { useAppDispatch } from "../../data/redux/hooks";
import { setReportWeek } from "../../data/redux/reportWeekSlice";
import { setError } from "../../data/redux/errorSlice";
import { error500Text } from "../../util/error-helper";
import "./ReportBook.scss";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

interface ReportWeekAttachmentsProps {
  reportWeek: ReportWeek | undefined;
  editable: boolean;
  userId: number;
  year: string | number;
  week: string | number;
}

const ReportWeekAttachments: React.FC<ReportWeekAttachmentsProps> = ({
  reportWeek,
  editable,
  userId,
  year,
  week,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const refetchReportWeek = async (mergeAttachmentsFrom?: ReportWeek) => {
    const res = await ReportService.getReportWeekEntries(userId, year, week);
    if (res?.report) {
      const report = res.report;
      const fromApi = normalizeAttachments(report.attachments);
      const mergedAttachments =
        mergeAttachmentsFrom && fromApi.length === 0
          ? normalizeAttachments(mergeAttachmentsFrom.attachments)
          : fromApi;
      dispatch(setReportWeek({ ...report, attachments: mergedAttachments }));
    }
  };

  /** API kann attachments als Array oder anders liefern – einheitliches Array daraus machen. */
  const normalizeAttachments = (att: unknown): ReportWeekAttachment[] => {
    if (Array.isArray(att)) {
      return att.map((a: any) => ({
        id: a.id,
        url: a.url ?? "",
        filename: a.filename ?? a.name ?? "Datei",
        content_type: a.content_type ?? a.mime_type,
      }));
    }
    return [];
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !reportWeek?.id) return;

    const toUpload: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!ACCEPTED_TYPES.includes(file.type)) {
        dispatch(setError({ isOpen: true, message: `„${file.name}“: Nur PDF, JPG und PNG sind erlaubt.` }));
        e.target.value = "";
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        dispatch(setError({ isOpen: true, message: `„${file.name}“: Maximale Dateigröße ist 10 MB.` }));
        e.target.value = "";
        return;
      }
      toUpload.push(file);
    }

    e.target.value = "";
    if (toUpload.length === 0) return;

    setUploading(true);
    try {
      for (const file of toUpload) {
        const res = await ReportService.uploadReportWeekAttachment(reportWeek.id!, file);
        if (res?.report) {
          dispatch(setReportWeek(res.report));
          if (!Array.isArray(res.report.days)) {
            await refetchReportWeek(res.report);
          }
        } else {
          await refetchReportWeek();
        }
      }
    } catch (err) {
      dispatch(setError({ isOpen: true, message: error500Text }));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachment: ReportWeekAttachment) => {
    if (!editable) return;
    setDeletingId(attachment.id);
    try {
      const res = await ReportService.deleteReportWeekAttachment(attachment.id);
      if (res?.report) {
        dispatch(setReportWeek(res.report));
        if (!Array.isArray(res.report.days)) {
          await refetchReportWeek(res.report);
        }
      } else {
        await refetchReportWeek();
      }
    } catch (err) {
      dispatch(setError({ isOpen: true, message: error500Text }));
    } finally {
      setDeletingId(null);
    }
  };

  /** Datei per API laden und als Download anbieten (nutzt gleiche HTTP-Schicht wie Rest der App). */
  const downloadAttachment = async (att: ReportWeekAttachment) => {
    setDownloadingId(att.id);
    try {
      const res = await ReportService.downloadReportWeekAttachment(att.id);
      const data = res?.data;
      if (data?.error) {
        dispatch(setError({ isOpen: true, message: data.error || "Datei konnte nicht geladen werden." }));
        return;
      }
      if (data == null) {
        dispatch(setError({ isOpen: true, message: "Datei konnte nicht geladen werden." }));
        return;
      }
      const fileUrl = typeof data === "string"
        ? `data:application/octet-stream;base64,${data}`
        : URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = att.filename || "Anhang";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (typeof data !== "string") URL.revokeObjectURL(fileUrl);
    } catch (err) {
      dispatch(setError({ isOpen: true, message: "Download fehlgeschlagen." }));
    } finally {
      setDownloadingId(null);
    }
  };

  const attachments = normalizeAttachments(reportWeek?.attachments);

  return (
    <IonCol size="12">
      <IonCard className="reportCard weekCard reportWeekAttachmentsCard">
        <IonCardHeader>
          <IonCardTitle>Anhänge</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {attachments.length > 0 && (
            <ul className="reportWeekAttachmentsList">
              {attachments.map((att) => (
                <li key={att.id} className="reportWeekAttachmentItem">
                  <button
                    type="button"
                    onClick={() => downloadAttachment(att)}
                    className="reportWeekAttachmentLink"
                    title="Herunterladen"
                    disabled={downloadingId === att.id}
                  >
                    <IonIcon icon={att.content_type === "application/pdf" ? documentOutline : imageOutline} />
                    <span>{att.filename}</span>
                    {downloadingId === att.id ? (
                      <IonSpinner name="crescent" className="reportWeekAttachmentDownloadIcon" />
                    ) : (
                      <IonIcon icon={downloadOutline} className="reportWeekAttachmentDownloadIcon" />
                    )}
                  </button>
                  {editable && (
                    <IonButton
                      fill="clear"
                      size="small"
                      color="danger"
                      onClick={() => handleDelete(att)}
                      disabled={deletingId === att.id}
                    >
                      {deletingId === att.id ? <IonSpinner name="crescent" /> : <IonIcon icon={trashOutline} />}
                    </IonButton>
                  )}
                </li>
              ))}
            </ul>
          )}
          {attachments.length === 0 && (
            <p className="reportWeekAttachmentsEmpty">Keine Anhänge</p>
          )}
          {editable && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/jpeg,image/png,image/jpg"
                multiple
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <IonButton
                fill="outline"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !reportWeek?.id}
              >
                {uploading ? <IonSpinner name="crescent" /> : <IonIcon icon={addOutline} />}
                {uploading ? " Wird hochgeladen…" : " Datei hinzufügen (max. 10 MB, PDF, JPG, PNG)"}
              </IonButton>
            </>
          )}
        </IonCardContent>
      </IonCard>
    </IonCol>
  );
};

export default ReportWeekAttachments;
